import apiClient, { handleApiResponse, handleApiError } from './apiClient';
import authService from './authService';
import { hasFeatureAccess, hasActionAccess } from '../utils/roleBasedAccess';

/**
 * Dashboard Service
 * Handles dashboard analytics and overview data with role-based access control
 */
class DashboardService {
  /**
   * Get current user role for access control
   * @returns {string|null} Current user role
   */
  async getCurrentUserRole() {
    try {
      const user = authService.getStoredUser() || await authService.getCurrentUser();
      return user?.role;
    } catch (error) {
      console.warn('[DashboardService] Could not get user role:', error);
      return null;
    }
  }

  /**
   * Filter dashboard data based on user role
   * @param {object} data - Raw dashboard data
   * @param {string} userRole - Current user role
   * @returns {object} Filtered dashboard data
   */
  filterDashboardDataByRole(data, userRole) {
    if (!userRole || !data) return data;

    const filteredData = { ...data };

    // Apply role-based data filtering
    switch (userRole) {
      case 'hr_user':
        // HR Users get limited dashboard data
        delete filteredData.budgetUtilization;
        delete filteredData.teamPerformanceDetailed;
        delete filteredData.salaryInsights;
        filteredData.isLimitedView = true;
        break;
        
      case 'hr_coordinator':
        // HR Coordinators get administrative metrics
        delete filteredData.budgetUtilization;
        delete filteredData.strategicMetrics;
        filteredData.coordinatorView = true;
        break;
        
      case 'recruiter':
        // Recruiters get recruitment-focused metrics
        delete filteredData.budgetUtilization;
        delete filteredData.teamManagementMetrics;
        filteredData.recruiterView = true;
        break;
        
      case 'hr_manager':
      case 'admin':
        // Managers and Admins get full access
        filteredData.fullAccess = true;
        break;
        
      default:
        // Unknown roles get minimal access
        return {
          totalCandidates: data.totalCandidates || 0,
          recentActivity: data.recentActivity || 0,
          isMinimalView: true
        };
    }

    return filteredData;
  }
  /**
   * Get dashboard overview data with role-based filtering
   * @returns {Promise<object>} Dashboard overview statistics filtered by role
   */
  async getDashboardOverview(userRole = null) {
    try {
      const params = new URLSearchParams();
      if (userRole) {
        params.append('user_role', userRole);
      }
      
      const response = await apiClient.get(`/dashboard/overview?${params}`);
      const data = handleApiResponse(response);
      
      // Ensure we always return an object
      return this.filterDashboardDataByRole(data, userRole);
    } catch (error) {
      // Return role-appropriate mock data if endpoint not available yet
      console.warn('Dashboard overview endpoint not available, returning mock data');
      const userRole = await this.getCurrentUserRole();
      
      const mockData = {
        totalCandidates: 150,
        activeCandidates: 45,
        interviewsScheduled: 12,
        averageScore: 72.5,
        recentActivity: 28,
        conversionRate: 35.2,
      };
      
      return this.filterDashboardDataByRole(mockData, userRole);
    }
  }

  /**
   * Get candidate statistics
   * @param {object} options - Query options
   * @returns {Promise<object>} Candidate statistics
   */
  async getCandidateStats(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.dateFrom) params.append('date_from', options.dateFrom);
      if (options.dateTo) params.append('date_to', options.dateTo);
      if (options.jobPosition) params.append('job_position', options.jobPosition);
      
      const response = await apiClient.get(`/dashboard/candidate-stats?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      // Return mock data if endpoint not available yet
      console.warn('Candidate stats endpoint not available, returning mock data');
      return {
        totalCandidates: 150,
        newThisMonth: 25,
        byStatus: {
          'new': 30,
          'screening': 45,
          'interview': 40,
          'offer': 15,
          'hired': 20,
        },
        byPosition: {
          'Software Engineer': 60,
          'Data Scientist': 35,
          'Product Manager': 25,
          'Designer': 30,
        },
        averageScores: {
          overall: 72.5,
          technical: 75.2,
          communication: 68.8,
          cultural: 74.1,
        },
      };
    }
  }

  /**
   * Get interview statistics
   * @param {object} options - Query options
   * @returns {Promise<object>} Interview statistics
   */
  async getInterviewStats(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.dateFrom) params.append('date_from', options.dateFrom);
      if (options.dateTo) params.append('date_to', options.dateTo);
      if (options.interviewerId) params.append('interviewer_id', options.interviewerId);
      
      const response = await apiClient.get(`/dashboard/interview-stats?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      // Return mock data if endpoint not available yet
      console.warn('Interview stats endpoint not available, returning mock data');
      return {
        totalInterviews: 85,
        completedThisWeek: 12,
        scheduledUpcoming: 18,
        averageRating: 4.2,
        byType: {
          'technical': 45,
          'behavioral': 25,
          'cultural': 15,
        },
        successRate: 65.5,
        averageDuration: 55, // minutes
      };
    }
  }

  /**
   * Get AI processing statistics
   * @param {object} options - Query options
   * @returns {Promise<object>} AI processing statistics
   */
  async getAiProcessingStats(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.dateFrom) params.append('date_from', options.dateFrom);
      if (options.dateTo) params.append('date_to', options.dateTo);
      
      const response = await apiClient.get(`/dashboard/ai-stats?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      // Return mock data if endpoint not available yet
      console.warn('AI stats endpoint not available, returning mock data');
      return {
        totalCvsProcessed: 125,
        processedThisWeek: 18,
        averageProcessingTime: 15, // seconds
        successRate: 97.5,
        topSkillsIdentified: [
          'JavaScript',
          'Python',
          'React',
          'Machine Learning',
          'Project Management',
        ],
        aiAccuracyScore: 92.3,
      };
    }
  }

  /**
   * Get recruitment pipeline data
   * @param {object} options - Query options
   * @returns {Promise<object>} Pipeline statistics
   */
  async getPipelineStats(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.dateFrom) params.append('date_from', options.dateFrom);
      if (options.dateTo) params.append('date_to', options.dateTo);
      if (options.jobPosition) params.append('job_position', options.jobPosition);
      
      const response = await apiClient.get(`/dashboard/pipeline-stats?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      // Return mock data if endpoint not available yet
      console.warn('Pipeline stats endpoint not available, returning mock data');
      return {
        stages: [
          { name: 'Applied', count: 150, percentage: 100 },
          { name: 'Screening', count: 85, percentage: 56.7 },
          { name: 'Interview', count: 45, percentage: 30.0 },
          { name: 'Final Round', count: 20, percentage: 13.3 },
          { name: 'Offer', count: 12, percentage: 8.0 },
          { name: 'Hired', count: 8, percentage: 5.3 },
        ],
        conversionRates: {
          'applied_to_screening': 56.7,
          'screening_to_interview': 52.9,
          'interview_to_final': 44.4,
          'final_to_offer': 60.0,
          'offer_to_hired': 66.7,
        },
        timeToHire: {
          average: 21, // days
          median: 18,
          fastest: 7,
          slowest: 45,
        },
      };
    }
  }

  /**
   * Get recent activity feed
   * @param {object} options - Query options
   * @returns {Promise<Array>} Recent activities
   */
  async getRecentActivity(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.limit) params.append('limit', options.limit);
      if (options.type) params.append('type', options.type);
      
      const response = await apiClient.get(`/dashboard/recent-activity?${params}`);
      const data = handleApiResponse(response);
      
      // Ensure we always return an array
      return Array.isArray(data) ? data : (data?.activities || data?.items || []);
    } catch (error) {
      // Return mock data if endpoint not available yet
      console.warn('Recent activity endpoint not available, returning mock data');
      return [
        {
          id: 1,
          type: 'candidate_submitted',
          title: 'New candidate application',
          description: 'Sarah Johnson submitted application for Software Engineer',
          timestamp: new Date().toISOString(),
          user: 'Sarah Johnson'
        },
        {
          id: 2,
          type: 'interview_scheduled',
          title: 'Interview scheduled',
          description: 'Interview scheduled for Alex Rodriguez - Senior Developer',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          user: 'Alex Rodriguez'
        },
        {
          id: 3,
          type: 'status_update',
          title: 'Status updated',
          description: 'Michael Chen moved to final round',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          user: 'Michael Chen'
        }
      ];
    }
  }

  /**
   * Get performance metrics over time
   * @param {object} options - Query options
   * @returns {Promise<Array>} Time series data
   */
  async getPerformanceMetrics(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.metric) params.append('metric', options.metric);
      if (options.period) params.append('period', options.period);
      if (options.dateFrom) params.append('date_from', options.dateFrom);
      if (options.dateTo) params.append('date_to', options.dateTo);
      
      const response = await apiClient.get(`/dashboard/performance-metrics?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      // Return mock data if endpoint not available yet
      console.warn('Performance metrics endpoint not available, returning mock data');
      return {
        labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        datasets: [
          {
            name: 'CVs Processed',
            data: [25, 35, 28, 42],
          },
          {
            name: 'Interviews Conducted',
            data: [15, 22, 18, 28],
          },
          {
            name: 'Candidates Hired',
            data: [3, 5, 2, 6],
          },
        ],
      };
    }
  }

  /**
   * Export dashboard data
   * @param {object} options - Export options
   * @returns {Promise<Blob>} Exported data file
   */
  async exportDashboardData(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.format) params.append('format', options.format);
      if (options.dateFrom) params.append('date_from', options.dateFrom);
      if (options.dateTo) params.append('date_to', options.dateTo);
      if (options.includeCharts) params.append('include_charts', options.includeCharts);
      
      const response = await apiClient.get(`/dashboard/export?${params}`, {
        responseType: 'blob',
      });
      
      return response.data;
    } catch (error) {
      handleApiError(error);
    }
  }
}

export default new DashboardService();